import {
  AddProductsToCurrentCartSucceededAction,
  AddToCurrentCartSucceededAction,
  DeleteCurrentCartEntrySucceededAction,
  RemoveGiftCardFromCartFailedAction,
  RemoveGiftCardFromCartSucceededAction,
  UpdateCurrentCartEntryQuantitySucceededAction,
  addProductsToCurrentCartSucceededType,
  addToCurrentCartSucceededType,
  changeCurrentCartPaymentModeRequested,
  deleteCurrentCartEntrySucceededType,
  removeGiftCardFromCartFailedType,
  removeGiftCardFromCartRequested,
  removeGiftCardFromCartSucceededType,
  updateCurrentCartEntryQuantitySucceededType
} from '../../carts/cartsSlice';
import { cancel, delay, fork, join, put, race, select } from 'typed-redux-saga';
import { ApiCartPaymentMode } from '../../api/cart/changeCartPaymentModeApi';
import { AppliedPaymentCode } from '@spa/generated/graphql/graphql';
import { CustomOrderType } from '@spa/lib/store/carts/CustomOrderType';
import { StateCart } from '../../carts/CartsState';
import { Task } from 'redux-saga';
import generateActionId from '../../../generateActionId';
import { giftCardReconciledAfterCartEntryChange } from '../../carts/cartCrossTabEvents';
import logger from '@spa/lib/logger';
import selectCarts from '../../carts/selectors/selectCarts';
import waitForSucceededOrFailedAction from '@spa/lib/store/sagas/session/waitForSucceededOrFailedAction';

type ReconcileGiftCardTriggerAction =
  | AddToCurrentCartSucceededAction
  | AddProductsToCurrentCartSucceededAction
  | UpdateCurrentCartEntryQuantitySucceededAction
  | DeleteCurrentCartEntrySucceededAction;

type GiftCardRemovalResult =
  | { readonly type: 'succeeded'; readonly action: RemoveGiftCardFromCartSucceededAction }
  | { readonly type: 'failed'; readonly action: RemoveGiftCardFromCartFailedAction };

const giftCardRemovalTimeoutMs = 30_000;

function* waitForGiftCardRemoval(actionId: number): Generator<unknown, GiftCardRemovalResult> {
  return yield* waitForSucceededOrFailedAction<
    typeof removeGiftCardFromCartSucceededType,
    typeof removeGiftCardFromCartFailedType,
    RemoveGiftCardFromCartSucceededAction,
    RemoveGiftCardFromCartFailedAction
  >(actionId, removeGiftCardFromCartSucceededType, removeGiftCardFromCartFailedType);
}

function* reconcileGiftCardForCart(customOrderType: CustomOrderType): Generator {
  const carts = yield* select(selectCarts);
  const cart: StateCart | null = carts[customOrderType];

  if (cart === null) {
    return;
  }

  const hasGiftCard = (cart.payments ?? []).some(
    (payment) => payment.code === AppliedPaymentCode.GIFT_CARD && payment.giftCard !== null
  );
  if (!hasGiftCard) {
    return;
  }

  const hasSelectedPaymentMethod =
    cart.payment !== null ||
    (cart.payments ?? []).some((payment) => payment.code !== AppliedPaymentCode.GIFT_CARD);

  const removeGiftCardActionId = generateActionId();
  const removeGiftCardTask: Task<GiftCardRemovalResult> = yield* fork(
    waitForGiftCardRemoval,
    removeGiftCardActionId
  );
  const paymentModeActionId = hasSelectedPaymentMethod ? generateActionId() : null;

  yield* put(
    removeGiftCardFromCartRequested({ actionId: removeGiftCardActionId, cartId: cart.code })
  );

  if (paymentModeActionId !== null) {
    yield* put(
      changeCurrentCartPaymentModeRequested({
        actionId: paymentModeActionId,
        customOrderType,
        paymentMode: ApiCartPaymentMode.NONE
      })
    );
  }

  const { removalResult: untypedRemovalResult, timedOut } = yield* race({
    removalResult: join(removeGiftCardTask),
    timedOut: delay(giftCardRemovalTimeoutMs, true)
  });
  const removalResult = untypedRemovalResult as GiftCardRemovalResult | undefined;

  if (timedOut === true) {
    yield* cancel(removeGiftCardTask);
    logger.warn(
      {
        actionId: removeGiftCardActionId,
        cartId: cart.code,
        customOrderType,
        timeoutMs: giftCardRemovalTimeoutMs
      },
      'Timed out waiting for gift card removal after cart entry change'
    );
  } else if (
    removalResult?.type === 'succeeded' &&
    removalResult.action.payload.cart !== null &&
    !(removalResult.action.payload.cart.payments ?? []).some(
      (payment) => payment.code === AppliedPaymentCode.GIFT_CARD && payment.giftCard !== null
    )
  ) {
    yield* put(giftCardReconciledAfterCartEntryChange());
  }
}

function* reconcileGiftCardOnCartEntryChangeSaga(
  action: ReconcileGiftCardTriggerAction
): Generator {
  yield* reconcileGiftCardForCart(action.payload.customOrderType);
}

export const reconcileGiftCardOnCartEntryChangeTriggerTypes = [
  addToCurrentCartSucceededType,
  addProductsToCurrentCartSucceededType,
  updateCurrentCartEntryQuantitySucceededType,
  deleteCurrentCartEntrySucceededType
];

export default reconcileGiftCardOnCartEntryChangeSaga;
